import { type Audience } from "@/lib/auth/currentUser";
import { specificIntegrationAction } from "./integrationLink";
import { COEXISTENCE_REASON_COPY } from "@/lib/whatsapp/coexistenceState";
import { isMetaRateLimit, metaErrorDetailOf, metaErrorText } from "./metaError";

export type ErrorSeverity = "info" | "warning" | "error" | "critical";

export type ErrorCategory =
  | "authentication"
  | "permission"
  | "integration"
  | "campaign"
  | "messaging"
  | "billing"
  | "validation"
  | "media"
  | "network"
  | "rate_limit"
  | "not_found"
  | "server"
  | "unknown";

export interface FriendlyError {
  title: string;
  description: string;
  severity?: ErrorSeverity;
  retryable?: boolean;
  category?: ErrorCategory;
  fixableBy?: "admin" | "support" | "anyone";
  action?: { label: string; to: string };
  whenCannotFix?: string;
}

export interface ApiErrorLike {
  error?: string | null;
  code?: string;
  message?: string;
  details?: string;
  meta?: { code?: string; subcode?: number | string; [k: string]: any } | null;
  statusCode?: number;
}

const CATALOG: Record<string, FriendlyError> = {
  // Super-admin Edit Subscription — business rules checked on save
  // (SubscriptionService._assertValidSubscriptionState).
  SUBSCRIPTION_PERIOD_DATES_REQUIRED: {
    title: "Add the period dates",
    description: "An active paid plan needs a start and end date. Use Fill dates from plan.",
    category: "validation",
  },
  SUBSCRIPTION_PERIOD_END_BEFORE_START: {
    title: "Check the dates",
    description: "Period end must be after period start.",
    category: "validation",
  },
  SUBSCRIPTION_ACTIVE_PERIOD_ENDED: {
    title: "End date has passed",
    description: "Pick a future end date, or set status to Expired.",
    category: "validation",
  },
  SUBSCRIPTION_PAID_PLAN_TRIAL: {
    title: "Paid plan can't be a trial",
    description: "Set status to Active and add the period dates.",
    category: "validation",
  },
  SUBSCRIPTION_TRIAL_END_REQUIRED: {
    title: "Trial end date missing",
    description: "Add a trial end date, or pick another status.",
    category: "validation",
  },
  SUBSCRIPTION_TRIAL_ENDED: {
    title: "Trial has ended",
    description: "Set status to Expired, or move the client to a paid plan.",
    category: "validation",
  },
  SUBSCRIPTION_INVALID_STATUS: {
    title: "Unknown status",
    description: "Pick a status from the list.",
    category: "validation",
  },
  WHATSAPP_WINDOW_EXPIRED: {
    title: "Reply window closed",
    description:
      "Over 24 hours passed since the customer's last message. Send an approved template instead.",
  },
  SOCIAL_WINDOW_EXPIRED: {
    title: "Reply window closed",
    description:
      "Over 24 hours passed since the customer's last message. Wait for them to message you again.",
    severity: "warning",
  },
  WHATSAPP_NOT_CONNECTED: {
    title: "WhatsApp not connected",
    description: "Your WhatsApp number is not connected yet. Please connect it first.",
    fixableBy: "admin",
    action: { label: "Connect WhatsApp", to: "/home/integrations/whatsapp-business" },
    whenCannotFix: "Your WhatsApp number is not connected yet. Ask your admin to connect it.",
  },
  WHATSAPP_RECONNECT_REQUIRED: {
    title: "Reconnect WhatsApp",
    description: "Your WhatsApp connection has expired. Please connect it again.",
    fixableBy: "admin",
    action: { label: "Reconnect WhatsApp", to: "/home/integrations/whatsapp-business" },
    whenCannotFix: "Your WhatsApp connection has expired. Ask your admin to connect it again.",
  },
  WHATSAPP_NUMBER_NOT_REGISTERED: {
    title: "WhatsApp number not set up",
    description: "Your WhatsApp number is not fully set up. Please connect it again.",
    fixableBy: "admin",
    action: { label: "Reconnect WhatsApp", to: "/home/integrations/whatsapp-business" },
    whenCannotFix: "Your WhatsApp number is not fully set up. Ask your admin to connect it again.",
  },
  // The backend blocked a send / template call because this number lost its
  // WeNext connection. WHATSAPP_DISCONNECTED_<REASON> variants are generated
  // below from COEXISTENCE_REASON_COPY so the toast names the cause.
  // Toast stays short (title + one line + button). The reason is shown in the
  // red connection banner, which stays on screen.
  WHATSAPP_DISCONNECTED: {
    title: "WhatsApp disconnected",
    description: "Reconnect this number to continue.",
    category: "integration",
    severity: "error",
    fixableBy: "admin",
    action: { label: "Reconnect", to: "/home/integrations/whatsapp-business" },
    whenCannotFix: "Ask your admin to reconnect this number.",
  },
  WHATSAPP_RECONNECTING: {
    title: "WhatsApp reconnecting",
    description: "Try again in a few minutes.",
    category: "integration",
    severity: "warning",
    retryable: true,
  },
  WHATSAPP_ACCESS_LOST: {
    title: "Can't send messages",
    description: "Your WhatsApp number is not connected. Please connect it again.",
    fixableBy: "admin",
    action: { label: "Reconnect WhatsApp", to: "/home/integrations/whatsapp-business" },
    whenCannotFix: "Your WhatsApp number is not connected. Ask your admin to connect it again.",
  },
  COMMERCE_STORE_URL_INVALID: {
    title: "Store address is wrong",
    description:
      "Your saved store address is wrong. Please connect the store again with the right address.",
    category: "integration",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix:
      "Your saved store address is wrong. Ask your admin to connect the store again with the right address.",
  },
  WORKSPACE_REQUIRED: {
    title: "No workspace selected",
    description: "No workspace is selected. Please choose a workspace.",
    fixableBy: "user",
    action: { label: "Open Workspaces", to: "/home/workspaces" },
    whenCannotFix: "No workspace is selected. Ask your admin to give you access to one.",
  },
  WORKSPACE_ACCESS_DENIED: {
    title: "No access to this workspace",
    description:
      "You aren't a member of the workspace this screen is trying to open. Switch to one of your workspaces, or ask an admin to add you to this one.",
    category: "permission",
    severity: "warning",
    fixableBy: "admin",
    action: { label: "Manage workspaces", to: "/home/workspaces" },
    whenCannotFix:
      "You aren't a member of this workspace. Ask an admin on your team to add you to it, then try again.",
  },
  BUSINESS_ACCOUNT_DISCONNECTED: {
    title: "Business account disconnected",
    description:
      "This business account is not connected. Please connect it again or pick another account.",
    fixableBy: "admin",
    action: { label: "Open Business Accounts", to: "/home/integrations/whatsapp-business" },
    whenCannotFix:
      "This business account is not connected. Ask your admin to connect it again.",
  },
  BUSINESS_ACCOUNT_UNAVAILABLE: {
    title: "Business account not available",
    description:
      "This business account is no longer available. Please pick a connected WhatsApp account.",
    fixableBy: "admin",
    action: { label: "Open Accounts", to: "/home/integrations/whatsapp-business" },
    whenCannotFix:
      "This business account is no longer available. Ask your admin to connect a WhatsApp account.",
  },
  AGENT_NOT_IN_WORKSPACE: {
    title: "Team member not in workspace",
    description:
      "This team member can't see this workspace. Pick someone else or give them access.",
  },
  ASSIGNEE_INACTIVE: {
    title: "Team member is inactive",
    description: "This team member is inactive. Please pick an active team member.",
    category: "validation",
    severity: "warning",
  },
  CONTACT_DUPLICATE_PHONE: {
    title: "Contact already exists",
    description: "This phone number is already saved as a contact. Open that contact instead.",
    category: "validation",
    severity: "warning",
  },
  CONTACT_BLOCKED: {
    title: "Contact is blocked",
    description: "This contact is blocked. Unblock them to send messages.",
  },
  TEMPLATE_NOT_ON_META: {
    title: "Template not approved yet",
    description:
      "This template is not approved by WhatsApp yet. Wait for approval or pick an approved template.",
  },
  TEMPLATE_NOT_FOUND: {
    title: "Template not found",
    description: "This template may have been deleted or renamed. Please pick another template.",
  },
  TEMPLATE_STALE: {
    title: "Template was changed",
    description: "Someone else changed this template. Refresh the page before saving.",
  },
  TEMPLATE_CATALOG_UNAVAILABLE: {
    title: "Templates not loading",
    description: "We couldn't load your templates. Please try again in a few minutes.",
  },
  TEMPLATE_INCOMPLETE: {
    title: "Template is incomplete",
    description:
      "Some parts of this template are empty. Fill in the header, body and button text.",
  },
  PRODUCT_NOT_IN_CATALOG: {
    title: "Product not in your catalog",
    description:
      "This product is not live in your catalog yet. Update your catalog in Commerce first.",
  },
  CATALOG_NOT_CONNECTED: {
    title: "No catalog connected",
    description: "No product catalog is connected. Connect one in Commerce settings.",
    fixableBy: "admin",
    whenCannotFix:
      "No product catalog is connected. Ask your admin to connect one in Commerce settings.",
  },
  CATALOG_NOT_ACCESSIBLE: {
    title: "Can't open catalog",
    description:
      "WhatsApp can't open this catalog. Check it is in the same business as your connected number.",
    fixableBy: "admin",
    whenCannotFix:
      "WhatsApp can't open this catalog. Ask your admin to check it is in the same business as your connected number.",
  },

  SUBSCRIPTION_EXPIRED: {
    title: "Your plan isn't active",
    description: "Your plan is not active. Choose a plan to continue — your data is safe.",
    category: "billing",
    severity: "warning",
    fixableBy: "admin",
    action: { label: "View plans", to: "/home/billing" },
    whenCannotFix: "Your plan is not active. Ask your admin to renew the plan.",
  },
  SUBSCRIPTION_STATE_UNAVAILABLE: {
    title: "Couldn't check your plan",
    description:
      "Your plan is fine, but we couldn't check it. Please try again in a few minutes.",
    category: "server",
    severity: "warning",
    retryable: true,
  },

  /* Zapier access (backend ZapierAccessService). */
  ZAPIER_DISABLED: {
    title: "Zapier unavailable",
    description: "Zapier is not available right now.",
    category: "integration",
    severity: "info",
  },
  ZAPIER_NOT_AVAILABLE: {
    title: "Zapier not available yet",
    description: "Zapier is not available for your account yet.",
    category: "integration",
    severity: "info",
  },
  ZAPIER_NOT_IN_PLAN: {
    title: "Zapier not in your plan",
    description: "Your plan doesn't include Zapier. Upgrade to use it.",
    category: "billing",
    severity: "info",
    fixableBy: "admin",
    action: { label: "View plans", to: "/home/billing" },
    whenCannotFix: "Your plan doesn't include Zapier. Ask your admin to upgrade.",
  },
  ENTITLEMENT_UNAVAILABLE: {
    title: "Couldn't check your plan",
    description: "We couldn't check your plan. Please try again in a few minutes.",
    category: "server",
    severity: "warning",
    retryable: true,
  },

  /* MoEngage access + key setup (backend MoengageAccessService, moengageRoutes). */
  MOENGAGE_DISABLED: {
    title: "MoEngage unavailable",
    description: "MoEngage is not available right now.",
    category: "integration",
    severity: "info",
  },
  MOENGAGE_NOT_AVAILABLE: {
    title: "MoEngage not available yet",
    description: "MoEngage is not available for your account yet.",
    category: "integration",
    severity: "info",
  },
  MOENGAGE_NOT_IN_PLAN: {
    title: "MoEngage not in your plan",
    description: "Your plan doesn't include MoEngage. Upgrade to use it.",
    category: "billing",
    severity: "info",
    fixableBy: "admin",
    action: { label: "View plans", to: "/home/billing" },
    whenCannotFix: "Your plan doesn't include MoEngage. Ask your admin to upgrade.",
  },
  MOENGAGE_WORKSPACE_INVALID: {
    title: "Pick a workspace",
    description: "Choose one of your workspaces and try again.",
    category: "validation",
  },
  MOENGAGE_NUMBER_INVALID: {
    title: "Pick a WhatsApp number",
    description: "Choose an active WhatsApp number in this workspace.",
    category: "validation",
  },
  MOENGAGE_DLR_URL_INVALID: {
    title: "Tracking URL not valid",
    description: "Paste the delivery tracking URL from MoEngage. It starts with https://api-0.",
    category: "validation",
  },
  MOENGAGE_KEY_LIMIT: {
    title: "Too many keys",
    description: "This number has 5 keys. Revoke one, then try again.",
    category: "validation",
  },
  MOENGAGE_KEY_NOT_FOUND: {
    title: "Key not found",
    description: "This key was already revoked. Refresh the page.",
    category: "not_found",
  },
  API_KEY_NAME_RESERVED: {
    title: "Name not allowed",
    description: "This key name is reserved. Choose another name.",
    category: "validation",
  },
  INTEGRATION_MANAGED_KEY: {
    title: "Managed elsewhere",
    description: "Manage this key from its integration page.",
    category: "permission",
  },
  ADMIN_REQUIRED: {
    title: "Admins only",
    description: "Only admins can do this. Ask your admin.",
    category: "permission",
  },

  ROLE_FORBIDDEN: {
    title: "Not allowed",
    description: "You don't have permission to do this. Ask your admin for access.",
  },
  UNAUTHORIZED: {
    title: "Please sign in again",
    description: "You have been signed out. Please sign in again.",
  },
  FORBIDDEN: {
    title: "Not allowed",
    description: "You don't have permission to do this. Ask your admin for access.",
  },

  NEEDS_RECONNECT: {
    title: "Reconnect required",
    description: "This account's connection has expired. Please connect it again in Integrations.",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix:
      "This account's connection has expired. Ask your admin to connect it again in Integrations.",
  },
  ACCOUNT_ORG_MISMATCH: {
    title: "Account not in this workspace",
    description:
      "This account belongs to another workspace. Please pick an account from this workspace.",
  },
  ACCOUNT_NOT_FOUND: {
    title: "Account not found",
    description: "We can't find this account. It may have been disconnected.",
  },
  DEV_TOKEN_NOT_APPROVED: {
    title: "Waiting for approval",
    description:
      "This connection is still waiting for approval. Live actions will work once it is approved.",
  },
  CSE_NOT_CONFIGURED: {
    title: "Search not available yet",
    description: "This feature is not turned on yet. Ask your admin to turn it on.",
  },
  AI_NOT_CONFIGURED: {
    title: "AI not set up",
    description: "AI is not set up yet. Please add the AI settings in Settings.",
    fixableBy: "admin",
    action: { label: "Open Settings", to: "/home/settings" },
    whenCannotFix: "AI is not set up yet. Ask your admin to add the AI settings.",
  },
  NO_AD_ACCOUNT: {
    title: "No ad account connected",
    description: "No ad account is connected. Please connect one first.",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix: "No ad account is connected. Ask your admin to connect one.",
  },
  META_PAYMENT_METHOD_REQUIRED: {
    title: "Unable to create ad",
    description: "Add a payment method to your Meta ad account in Billing & payments, then try again.",
  },
  META_CREATE_UNCONFIRMED: {
    title: "Meta didn't confirm",
    description: "Meta didn't confirm whether it was created. Refresh the page and check before trying again, so it isn't created twice.",
    category: "integration",
    severity: "warning",
  },
  AD_ACCOUNT_LIMIT_REACHED: {
    title: "Ad account limit reached",
    description:
      "Your plan doesn't include another Meta ad account. Disconnect one you no longer use, or upgrade to connect more.",
    category: "billing",
    severity: "warning",
    fixableBy: "admin",
    whenCannotFix:
      "Your plan doesn't include another Meta ad account. Ask your admin to upgrade, or to disconnect one you no longer use.",
  },
  PAUSE_LOOKUP_FAILED: {
    title: "Nothing was disconnected",
    description:
      "We couldn't reach Meta to pause this account's campaigns, so nothing was disconnected. Try again, or choose to leave them running.",
    category: "integration",
    severity: "warning",
    retryable: true,
  },
  AD_ACCOUNT_LIMIT_UNAVAILABLE: {
    title: "Couldn't check your plan",
    description: "We couldn't verify your plan's ad-account limit. Please try again.",
    category: "server",
    severity: "warning",
    retryable: true,
  },
  AD_ACCOUNT_CONNECTED_ELSEWHERE: {
    title: "Ad account in use elsewhere",
    description:
      "This ad account is already connected in another workspace. Disconnect it there first.",
    category: "integration",
    severity: "warning",
  },
  DRAFT_OTHER_ACCOUNT: {
    title: "Draft belongs to another ad account",
    description:
      "This draft was started on a different ad account. Switch back to that ad account to keep working on it.",
    category: "validation",
    severity: "warning",
  },
  AD_ACCOUNT_NOT_CONNECTED: {
    title: "Ad account not connected",
    description:
      "This ad account isn't connected to this workspace anymore. Pick another account.",
    category: "integration",
    severity: "warning",
  },
  AD_ACCOUNT_DISCONNECTED: {
    title: "Ad account disconnected",
    description:
      "This campaign belongs to an ad account that was disconnected from this workspace. Reconnect it to sync or change this campaign.",
    category: "integration",
    severity: "warning",
  },
  AD_ACCOUNT_SELECTION_UNAVAILABLE: {
    title: "Couldn't check the selected ad account",
    description: "We couldn't check the selected ad account just now. Please try again.",
    category: "server",
    severity: "warning",
    retryable: true,
  },
  AD_ACCOUNT_STATE_UNAVAILABLE: {
    title: "Couldn't load ad account settings",
    description: "We couldn't read your ad-account settings just now. Please try again.",
    category: "server",
    severity: "warning",
    retryable: true,
  },
  AD_ACCOUNT_AMBIGUOUS: {
    title: "Couldn't tell which ad account this belongs to",
    description:
      "This campaign isn't linked to a specific Meta ad account and this workspace has more than one in use, so nothing was changed. Open it from the ad account it was created in and try again.",
    category: "validation",
    severity: "warning",
  },
  RAW_LEAD_DATASET_REQUIRED: {
    title: "No dataset for this ad account",
    description:
      "This ad account has no Meta dataset yet, so new leads can't be reported to Meta. Connect a dataset for it first, then turn this on.",
    category: "integration",
    severity: "warning",
    fixableBy: "admin",
    whenCannotFix:
      "This ad account has no Meta dataset yet. Ask an admin on your team to connect one.",
  },
  NO_AI_PHONE_NUMBER: {
    title: "No AI phone number",
    description: "No AI phone number is set up. Please set one up before calling.",
  },

  RATE_LIMITED: {
    title: "Slow down a moment",
    description: "You're doing this too fast. Please wait a minute and try again.",
  },
  QUOTA_EXCEEDED: {
    title: "Daily limit reached",
    description: "You've reached your limit for now. Please try again later.",
  },
  META_RATE_LIMITED: {
    title: "Facebook temporarily busy",
    description: "You've reached Facebook's hourly limit. Please try again in a little while.",
  },
  QUEUE_UNAVAILABLE: {
    title: "Service busy",
    description: "Our system is busy right now. Please try again in a few minutes.",
  },

  FLOW_IN_USE: {
    title: "Flow is in use",
    description: "This flow is running, so it can't be changed. Pause it first.",
  },
  STATUS_IN_USE: {
    title: "Status in use",
    description: "Some records use this status. Move them to another status before deleting it.",
  },
  PHONE_ALREADY_IN_USE: {
    title: "Phone number already in use",
    description: "This phone number is used by another account. Enter a different number.",
    category: "validation",
    severity: "warning",
    fixableBy: "anyone",
  },
  EMAIL_TAKEN: {
    title: "Email already in use",
    description: "This email is used by another account. Enter a different email.",
    category: "validation",
    severity: "warning",
    fixableBy: "anyone",
  },
  SITE_SLUG_CONFLICT: {
    title: "Address already taken",
    description: "This website address is already taken. Please pick another one.",
  },

  INVALID_AMOUNT: {
    title: "Invalid amount",
    description: "This amount is not valid. Enter an amount more than 0.",
  },
  AMOUNT_INVALID_DECIMALS: {
    title: "Too many decimal places",
    description: "The amount has too many decimal places. Use at most 2, like 22.34.",
  },
  AMOUNT_TOO_LARGE: {
    title: "Amount too large",
    description: "This amount is too large. Please enter a smaller amount.",
  },

  RAZORPAY_NOT_CONNECTED: {
    title: "Razorpay not connected",
    description: "Razorpay is not connected. Connect it to accept payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect Razorpay", to: "/home/razorpay" },
    whenCannotFix: "Razorpay is not connected. Ask your admin to connect it.",
  },
  PAYU_NOT_CONNECTED: {
    title: "PayU not connected",
    description: "PayU is not connected. Connect it to accept payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect PayU", to: "/home/payu" },
    whenCannotFix: "PayU is not connected. Ask your admin to connect it.",
  },
  CASHFREE_NOT_CONNECTED: {
    title: "Cashfree not connected",
    description: "Cashfree is not connected. Connect it to accept payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect Cashfree", to: "/home/cashfree" },
    whenCannotFix: "Cashfree is not connected. Ask your admin to connect it.",
  },
  PAYMENT_NOT_CONNECTED: {
    title: "No payment method connected",
    description: "No payment provider is connected. Connect Razorpay, PayU or Cashfree to take payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix: "No payment provider is connected. Ask your admin to connect one.",
  },

  PAYMENT_PLAN_NOT_CONFIGURED: {
    title: "Plan not ready yet",
    description: "This plan is not fully set up yet. Please contact support.",
    category: "billing",
  },
  PAYMENT_GATEWAY_NOT_CONFIGURED: {
    title: "Payments aren't set up yet",
    description: "Payments are not turned on for your account. Please contact support.",
    category: "billing",
  },
  PAYMENT_GATEWAY_ERROR: {
    title: "Payments not working right now",
    description: "Our payment service is not working right now. Please try again in a few minutes.",
    category: "billing",
    retryable: true,
    fixableBy: "support",
  },
  PLAN_NOT_FOUND: {
    title: "Plan not found",
    description: "We can't find this plan. Refresh the page and pick a plan again.",
    category: "billing",
  },
  CHECKOUT_IN_PROGRESS: {
    title: "Payment already in progress",
    description: "You already have a payment open. Finish it or wait a minute and try again.",
    category: "billing",
    severity: "info",
    retryable: true,
    fixableBy: "anyone",
  },
  CHECKOUT_UNAVAILABLE: {
    title: "Couldn't start payment",
    description: "Something went wrong on our side and no money was taken. Please try again in a few minutes.",
    category: "billing",
    retryable: true,
    fixableBy: "support",
  },
  SUBSCRIPTION_ACTION_FAILED: {
    title: "Couldn't update your plan",
    description: "We couldn't update your plan. Please try again in a few minutes.",
    category: "billing",
    retryable: true,
  },
  // "Renew Now" (POST /subscriptions/renew and /renew/verify).
  RENEWAL_NOT_AVAILABLE: {
    title: "Renewal not open",
    description: "Renewal isn't open for your plan right now. Choose a plan instead.",
    category: "billing",
    severity: "info",
    action: { label: "Choose a plan", to: "/home/account-settings?section=plans" },
  },
  RENEWAL_PAYMENT_PROCESSING: {
    title: "Payment being confirmed",
    description: "Your last payment is still being confirmed. Wait a minute and check again.",
    category: "billing",
    severity: "info",
    retryable: true,
  },
  RENEWAL_VERIFY_INVALID: {
    title: "Couldn't confirm payment",
    description: "We couldn't confirm this payment. If money was taken, your plan will update in a few minutes.",
    category: "billing",
    retryable: true,
    fixableBy: "support",
  },
  RENEWAL_NOT_ALLOWED: {
    title: "Only admins can renew",
    description: "Only the account owner or an admin can renew the plan.",
    category: "billing",
    severity: "info",
  },

  PAYMENT_ALREADY_REFUNDED: {
    title: "Already refunded",
    description: "This payment was already refunded. You don't need to do anything.",
  },
  PAYMENT_NOT_CAPTURED: {
    title: "Payment not complete",
    description: "This payment is not complete yet. It can't be refunded until it is.",
  },
  INSUFFICIENT_BALANCE: {
    title: "Not enough balance",
    description: "Not enough balance for this refund. Add money to your payment account and try again.",
  },

  VIDEO_TOO_LARGE: {
    title: "Video too large",
    description: "This video is too big. Please pick a smaller file.",
  },
  PHOTO_TOO_LARGE: {
    title: "Image too large",
    description: "This image is too big. Please use a smaller file.",
  },
  FILE_TOO_LARGE: {
    title: "File too large",
    description: "This file is too big to upload. Please use a smaller file.",
    category: "media",
    severity: "warning",
  },
  TITLE_TOO_LONG: {
    title: "Title too long",
    description: "The title is too long. Please make it shorter.",
  },
  INVALID_VIDEO_FORMAT: {
    title: "Unsupported video",
    description: "This video type is not supported. Please use an MP4 video.",
  },
  INVALID_PHOTO_FORMAT: {
    title: "Unsupported image",
    description: "This image type is not supported. Please use JPG or PNG.",
  },

  OFFLINE: {
    title: "You're offline",
    description: "You are not connected to the internet. Reconnect and try again.",
    severity: "warning",
    retryable: true,
    category: "network",
  },
  NETWORK_ERROR: {
    title: "Connection problem",
    description: "We couldn't connect. Check your internet and try again.",
    severity: "warning",
    retryable: true,
    category: "network",
  },
  TIMEOUT: {
    title: "Timed out",
    description: "WeNext took too long to respond. Please try again.",
  },
  MICROSERVICE_ERROR: {
    title: "Something went wrong",
    description: "Something went wrong on our side. Please try again in a few minutes.",
  },
  VALIDATION_ERROR: {
    title: "Check your input",
    description: "Some details are not right. Please check the form and try again.",
  },
  VALIDATION_FAILED: {
    title: "Check your input",
    description: "Some details are not right. Please check the form and try again.",
  },
  INTERNAL_ERROR: {
    title: "Something went wrong",
    description: "We're temporarily having trouble completing your request. Please try again in a few minutes.",
    category: "server",
    severity: "error",
    retryable: true,
  },
  SESSION_CHECK_UNAVAILABLE: {
    title: "Temporary problem",
    description: "We're temporarily having trouble completing your request. Please try again in a few minutes.",
    category: "server",
    severity: "warning",
    retryable: true,
  },

  PHONE_NUMBER_RESTRICTED: {
    title: "Number restricted",
    description: "WhatsApp has limited this number for now. Check its status in WhatsApp Manager.",
  },
  PHONE_NUMBER_BANNED: {
    title: "Number banned",
    description: "WhatsApp has banned this number. Please contact support.",
  },
  PHONE_QUALITY_LOW: {
    title: "Number quality is low",
    description:
      "Customers marked your messages as spam. Send fewer campaigns until quality improves.",
  },
  MESSAGE_LIMIT_REACHED: {
    title: "Daily message limit reached",
    description:
      "You've reached your WhatsApp message limit. Please wait up to 24 hours for it to reset.",
  },
  MARKETING_MESSAGE_BLOCKED: {
    title: "Marketing message blocked",
    description:
      "WhatsApp held back this marketing message for this customer. Try later or send a normal message.",
  },
  USER_OPTED_OUT: {
    title: "Customer opted out",
    description:
      "This customer chose to stop your messages. You can't message them until they allow it again.",
  },
  RECIPIENT_NOT_ON_WHATSAPP: {
    title: "Not on WhatsApp",
    description: "This number is not on WhatsApp. Please check the number.",
  },
  INVALID_PHONE_NUMBER: {
    title: "Invalid phone number",
    description:
      "This phone number is not valid. Add the country code and remove spaces or symbols.",
  },
  TEMPLATE_PAUSED: {
    title: "Template paused",
    description:
      "This template was paused because of low quality. Use another approved template or edit this one.",
  },
  TEMPLATE_DISABLED: {
    title: "Template disabled",
    description:
      "WhatsApp turned off this template for breaking its rules. Create a new template that follows them.",
  },
  MEDIA_UPLOAD_EXPIRED: {
    title: "File expired",
    description: "This file is no longer available. Please upload it again.",
  },
  MEDIA_TYPE_NOT_SUPPORTED: {
    title: "File type not supported",
    description: "WhatsApp doesn't accept this type of file. Please send a different file.",
    category: "media",
    severity: "warning",
  },
  MEDIA_SEND_FAILED: {
    title: "File not sent",
    description: "WhatsApp couldn't send this file. Please check the file and try again.",
    category: "media",
    severity: "error",
  },
  UNSUPPORTED_MESSAGE: {
    title: "Message type not supported",
    description: "This kind of message can't be sent here. Try text, a photo, or a template.",
  },
  TEMPLATE_PARAMS_MISMATCH: {
    title: "Template details don't match",
    description:
      "The values you entered don't fit this template. Refresh the template and enter them again.",
  },
  TEMPLATE_TOO_LONG: {
    title: "Template message too long",
    description: "This message is too long for WhatsApp. Shorten the values you filled in.",
  },
  ACCOUNT_BILLING_ISSUE: {
    title: "Billing issue on this account",
    description:
      "Your WhatsApp Business account has a billing problem. Fix it in WhatsApp Manager before sending.",
  },
  CANT_MESSAGE_RECIPIENT: {
    title: "Can't message this number",
    description:
      "WhatsApp can't deliver to this number right now. Check the number is right and not your own.",
  },
  REQUEST_REJECTED: {
    title: "Couldn't complete this",
    description: "We couldn't complete this. Please refresh the page and try again.",
  },

  INSTAGRAM_NOT_CONNECTED: {
    title: "Instagram not connected",
    description: "No Instagram account is connected. Please connect one in Integrations.",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix: "No Instagram account is connected. Ask your admin to connect one.",
  },
  PAGE_DISCONNECTED: {
    title: "Facebook Page disconnected",
    description: "This Facebook Page is not connected. Please connect it again in Integrations.",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix: "This Facebook Page is not connected. Ask your admin to connect it again.",
  },
  AD_LOCATIONS_CONFLICT: {
    title: "Some locations overlap",
    description:
      "One of your locations is inside another one, for example a pincode inside a city you already picked. Remove the smaller one and try again.",
  },
  INSTAGRAM_PERMISSION_DENIED: {
    title: "Missing permissions",
    description:
      "Some Instagram or Facebook permissions are missing. Reconnect and allow all permissions.",
  },
  PAGE_PUBLISH_AUTH_FAILED: {
    title: "Can't post to this Page",
    description: "We can't post on this Page yet. Reconnect it and allow posting.",
  },
  ACCOUNT_RESTRICTED: {
    title: "Account restricted",
    description: "This account is limited for now. Check its status in Meta Business Suite.",
  },
  CONTENT_POLICY_VIOLATION: {
    title: "Content not allowed",
    description: "This content is against the rules. Please edit it and try again.",
  },
  POST_SCHEDULING_FAILED: {
    title: "Couldn't schedule post",
    description: "We couldn't schedule this post. Check the date, time and connected account, then try again.",
  },

  AD_ACCOUNT_DISABLED: {
    title: "Ad account disabled",
    description: "Meta has disabled this ad account. Fix it in Meta Ads Manager before running ads.",
  },
  AD_ACCOUNT_RESTRICTED: {
    title: "Ad account restricted",
    description: "This ad account is restricted. Check Meta Ads Manager for the required steps.",
  },
  INSUFFICIENT_AD_BALANCE: {
    title: "Not enough ad balance",
    description: "Your ad account balance is too low. Add funds and try again.",
  },
  CAMPAIGN_BUDGET_EXCEEDED: {
    title: "Budget used up",
    description: "This campaign has used all its budget. Increase the budget to keep it running.",
  },
  CAMPAIGN_LIMIT_REACHED: {
    title: "Campaign limit reached",
    description: "You have the most campaigns allowed. Pause or delete one to create a new one.",
  },
  SCHEDULE_TIME_INVALID: {
    title: "Invalid time",
    description: "We couldn't read that date and time. Please pick the send time again.",
  },
  SCHEDULE_TIME_IN_PAST: {
    title: "Time is in the past",
    description: "This time has already passed. Pick a time in the future.",
  },
  CAMPAIGN_STARTING_NOW: {
    title: "Campaign is starting",
    description: "This campaign is about to send. It can't be rescheduled now.",
  },
  CAMPAIGN_CANCEL_TOO_LATE: {
    title: "Campaign is starting",
    description: "This campaign is about to send. It can't be cancelled now.",
  },
  CAMPAIGN_NOT_CANCELLABLE: {
    title: "Can't cancel",
    description: "This campaign has already started or finished. It can't be cancelled.",
  },
  TAG_NOT_FOUND: {
    title: "Tag not found",
    description: "Some selected tags were deleted. Refresh and pick the tags again.",
  },
  PIXEL_NOT_FOUND: {
    title: "Pixel not found",
    description: "No Meta Pixel is set up for this account. Add a Pixel to track results.",
  },
  AUDIENCE_TOO_SMALL: {
    title: "Audience too small",
    description: "This audience is too small for an ad. Target more people and try again.",
  },

  FLOW_LOOP_DETECTED: {
    title: "Flow goes in a circle",
    description: "This flow goes in a circle and can't run. Remove the connection that loops back.",
  },
  FLOW_PUBLISH_FAILED: {
    title: "Couldn't publish flow",
    description: "We couldn't publish this flow. Make sure every step is filled in and connected.",
  },
  FLOW_TRIGGER_MISSING: {
    title: "Flow needs a trigger",
    description: "This flow has no starting trigger. Add one before publishing.",
  },
  FLOW_NODE_INVALID: {
    title: "A flow step is incomplete",
    description: "A step is missing required details. Open each step and fill in the marked fields.",
  },
  FLOW_EXECUTION_FAILED: {
    title: "Flow stopped",
    description: "This flow stopped before finishing. Check its steps and try again.",
  },
  WEBHOOK_FAILED: {
    title: "Couldn't reach your link",
    description: "We couldn't reach the link you added. Check it is online and try again.",
  },

  WEBHOOK_TIMEOUT: {
    title: "Your link didn't answer",
    description: "The link you added took too long to answer. Check it is online and try again.",
  },
  WEBHOOK_OVERRIDE_REJECTED: {
    title: "Link not accepted",
    description:
      "WhatsApp couldn't verify this link. Check it is online and the verify code matches.",
    category: "integration",
    severity: "warning",
    retryable: true,
  },
  WEBHOOK_SIGNATURE_INVALID: {
    title: "Couldn't verify the connection",
    description: "We couldn't safely check data from this app. Please connect it again.",
  },
  THIRD_PARTY_SERVICE_DOWN: {
    title: "Service unavailable",
    description: "A connected app is down right now. Please try again in a few minutes.",
  },
  INTEGRATION_DISCONNECTED: {
    title: "Integration disconnected",
    description: "This app is no longer connected. Please connect it again in Integrations.",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix:
      "This app is no longer connected. Ask your admin to connect it again in Integrations.",
  },

  FILE_CORRUPTED: {
    title: "File is damaged",
    description: "This file is damaged and can't be used. Please try another copy.",
  },
  FILE_UPLOAD_FAILED: {
    title: "Upload failed",
    description: "We couldn't upload this file. Check your connection and try again.",
  },
  FILE_SCAN_FAILED: {
    title: "Couldn't check file",
    description: "We couldn't safety-check this file. Try again or use another file.",
  },
  MEDIA_PROCESSING_FAILED: {
    title: "Couldn't use this file",
    description: "We couldn't process this file. Try a different file or type.",
  },

  CONFIG_MISSING: {
    title: "Not set up yet",
    description: "This feature is not fully set up. Please finish it in Settings.",
    fixableBy: "admin",
    action: { label: "Open Settings", to: "/home/settings" },
    whenCannotFix: "This feature is not fully set up. Ask your admin to finish it in Settings.",
  },
  AUTH_ERROR: {
    title: "Please sign in again",
    description: "You have been signed out. Please sign in again.",
  },
  GOOGLE_REJECTED: {
    title: "Not accepted by Google",
    description: "Google didn't accept this. Change what you entered and try again.",
  },
  INVALID_QUERY: {
    title: "Check your search",
    description: "This search didn't work. Change the words and try again.",
  },
  TIKTOK_IDENTITY_DEPRECATED: {
    title: "Reconnect TikTok",
    description: "Your TikTok connection needs a refresh. Please connect it again in Integrations.",
  },
  VIDEO_CREATE_NOT_SUPPORTED: {
    title: "Video not supported here",
    description: "This account can't create video posts from here yet.",
  },
  INSTANTIATE_FAILED_ROLLED_BACK: {
    title: "Couldn't create it",
    description: "Something went wrong, so nothing was saved. Please try again.",
  },

  access_token_invalid: {
    title: "Reconnect required",
    description: "This account's connection has expired. Please connect it again in Integrations.",
  },
  scope_not_authorized: {
    title: "Permission missing",
    description: "A needed permission was not given. Reconnect and allow all permissions.",
  },
  token_not_authorized_for_specified_publish_id: {
    title: "Reconnect TikTok",
    description: "We can't open this TikTok post anymore. Please connect TikTok again.",
  },
  spam_risk_too_many_posts: {
    title: "TikTok post limit reached",
    description: "TikTok has paused posting for this account for now. Wait a while before posting again.",
    category: "rate_limit",
    severity: "info",
  },
  spam_risk_user_banned_from_posting: {
    title: "Posting blocked by TikTok",
    description: "TikTok has blocked this account from posting. Check the account in the TikTok app.",
  },
  reached_active_user_cap: {
    title: "Too many uploads at once",
    description: "TikTok is still working on your recent uploads. Wait for them to finish, then try again.",
  },
  unaudited_client_can_only_post_to_private_accounts: {
    title: "Public posting not ready yet",
    description:
      "For now, posts can only go to private TikTok accounts. Make the account private or contact support.",
  },
  url_ownership_unverified: {
    title: "Verify your website with TikTok",
    description:
      "TikTok must verify the website in your link. Verify it in your TikTok developer settings.",
  },
  privacy_level_option_mismatch: {
    title: "Pick a privacy setting",
    description: "This privacy setting can't be used. Choose public, friends, or private and try again.",
  },
  rate_limit_exceeded: {
    title: "Slow down a moment",
    description: "You're doing this too fast. Please wait a minute and try again.",
  },

  UNSUPPORTED_VIDEO_FORMAT: {
    title: "Unsupported video",
    description: "This video type is not supported. Please use an MP4 (H.264) video.",
  },
  INVALID_VIDEO_SIZE: {
    title: "Video size not allowed",
    description: "This video's size is not allowed. Please use a smaller file.",
  },
  VIDEO_DOWNLOAD_FAILED: {
    title: "Couldn't get the video",
    description: "We couldn't get the video to upload it. Check the video link and try again.",
  },
  EMPTY_VIDEO: {
    title: "Video file is empty",
    description: "This video file is empty. Save it again or pick another file.",
  },
  TIKTOK_CONTENT_ERROR: {
    title: "TikTok couldn't use this",
    description: "TikTok didn't accept this post. Check the video, photo and caption, then try again.",
  },
  TIKTOK_ERROR: {
    title: "TikTok problem",
    description: "TikTok had a problem. Please try again in a few minutes.",
  },

  YOUTUBE_NO_REFRESH_TOKEN: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection needs a refresh. Disconnect and connect the channel again in Integrations.",
  },
  YOUTUBE_REFRESH_NOT_AVAILABLE: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection has expired. Please connect the channel again in Integrations.",
  },
  YOUTUBE_TOKEN_EXCHANGE_FAILED: {
    title: "Couldn't connect YouTube",
    description: "We couldn't finish connecting YouTube. Please try connecting the channel again.",
  },
  YOUTUBE_NO_ACCESS_TOKEN: {
    title: "Reconnect YouTube",
    description: "YouTube sign-in didn't give us access. Please connect the channel again in Integrations.",
  },
  YOUTUBE_NO_CHANNEL: {
    title: "No YouTube channel found",
    description: "This Google account has no YouTube channel. Create one on YouTube, then connect again.",
  },
  YOUTUBE_CHANNEL_FETCH_FAILED: {
    title: "Couldn't load your channel",
    description: "We couldn't load your YouTube channel. Please try again in a few minutes.",
  },
  YOUTUBE_CHANNEL_IN_OTHER_ORG: {
    title: "Channel already connected",
    description: "This YouTube channel is connected to another workspace. Disconnect it there first.",
  },
  YOUTUBE_INVALID_CHANNEL_RESPONSE: {
    title: "Couldn't read your channel",
    description: "YouTube sent back details we can't read. Please connect the channel again.",
  },

  IMAGE_UPLOAD_FAILED: {
    title: "Image upload failed",
    description: "We couldn't upload this image. Check the file and try again.",
  },
  IMAGE_FETCH_FAILED: {
    title: "Couldn't get the image",
    description: "We couldn't load this image. Check the image link and try again.",
  },

  RAZORPAY_API_ERROR: {
    title: "Razorpay payment problem",
    description: "Razorpay had a problem. Please try again in a few minutes.",
  },
  RAZORPAY_INVALID_CREDENTIALS: {
    title: "Razorpay keys not accepted",
    description:
      "Razorpay didn't accept these keys. Check your Key ID and Key Secret in Razorpay and try again.",
  },

  TOKEN_EXCHANGE_REJECTED: {
    title: "Shopify didn't accept these details",
    description:
      "Shopify didn't accept this Client ID and Client Secret. Check both and try again.",
    category: "authentication",
    severity: "warning",
  },
  CLIENT_CREDENTIALS_NOT_APPLICABLE: {
    title: "Shopify didn't accept these details",
    description:
      "Shopify didn't accept this Client ID and Client Secret. Check both, or use the Access token option instead.",
    category: "authentication",
    severity: "warning",
  },
  TOKEN_EXCHANGE_UNAUTHORIZED: {
    title: "Shopify didn't accept these details",
    description:
      "Shopify didn't accept these details. Check both keys and that the app is installed on this store.",
    category: "authentication",
    severity: "warning",
  },
  SHOP_NOT_PERMITTED: {
    title: "This method won't work here",
    description:
      "This store can't connect with Client ID and Secret. Use the Access token option instead.",
    category: "integration",
    severity: "warning",
  },
  TOKEN_EXCHANGE_FAILED: {
    title: "Couldn't reach Shopify",
    description:
      "Shopify didn't answer. Your details may be fine — please try again in a few minutes.",
    category: "network",
    severity: "warning",
    retryable: true,
  },
  TOKEN_EXCHANGE_NO_TOKEN: {
    title: "Shopify didn't finish connecting",
    description:
      "Shopify accepted your details but didn't finish. Try again, or use the Access token option.",
    category: "integration",
    retryable: true,
  },

  INVALID_SHOP_DOMAIN: {
    title: "Not a Shopify store address",
    description:
      "This is not a .myshopify.com address. Enter one like cool-store.myshopify.com.",
    category: "validation",
    severity: "warning",
  },
  INVALID_ACCESS_TOKEN: {
    title: "Access token not accepted",
    description:
      "Shopify didn't accept this access token. Copy it again from your Shopify app — it starts with shpat_.",
    category: "authentication",
    severity: "warning",
  },
  SHOP_NOT_FOUND: {
    title: "Store not found",
    description:
      "No Shopify store has this address. Use the .myshopify.com address from Settings → Domains.",
    category: "validation",
    severity: "warning",
  },
  SHOP_UNREACHABLE: {
    title: "Couldn't reach that store",
    description:
      "Nothing answered at this store address. Check it matches your .myshopify.com address and try again.",
    category: "network",
    severity: "warning",
    retryable: true,
  },
  SHOP_PROBE_FAILED: {
    title: "Couldn't open your store",
    description:
      "Your Shopify store may be paused or have an unpaid bill. Check it opens in Shopify, then try again.",
    category: "integration",
    severity: "warning",
    retryable: true,
  },

  TEMPLATE_LIMIT_REACHED: {
    title: "Template limit reached",
    description: "You have the most templates allowed. Delete one you don't use, then try again.",
  },
  TEMPLATE_FIELD_TOO_LONG: {
    title: "Template text is too long",
    description: "Some template text is too long. Shorten the header, body, or footer.",
  },
  TEMPLATE_FORMAT_INVALID: {
    title: "Template format is wrong",
    description: "The header, body, or footer format is wrong. Check the template and try again.",
  },
  TEMPLATE_TOO_MANY_VARIABLES: {
    title: "Too many variables",
    description: "This template has too many {{variables}} for its length. Add more text or remove some.",
  },
  TEMPLATE_VARIABLE_POSITION: {
    title: "Move the variable",
    description: "A {{variable}} can't be at the very start or end of the template. Add some text around it.",
  },

  FLOW_BLOCKED: {
    title: "This flow is blocked",
    description: "WhatsApp has blocked this flow, so it can't be used. Check its status in WhatsApp Manager.",
  },
  FLOW_THROTTLED: {
    title: "Flow is sending too fast",
    description: "This flow reached its hourly limit. Please wait a while and try again.",
  },

  COUNTRY_RESTRICTED: {
    title: "Messaging restricted in this region",
    description: "Messaging is restricted in this customer's country. Contact support if you need help.",
  },
  PAYMENTS_TOS_NOT_ACCEPTED: {
    title: "Accept WhatsApp Payments terms",
    description: "WhatsApp Payments is not fully set up. Accept the payment terms in WhatsApp Manager.",
  },

  IG_MEDIA_FETCH_TIMEOUT: {
    title: "Couldn't get your photo or video",
    description: "Instagram couldn't download your photo or video in time. Use a smaller file and try again.",
  },
  IG_IMAGE_TOO_LARGE: {
    title: "Image too large",
    description: "Instagram images must be under 8 MB. Please use a smaller image.",
  },
  IG_UNSUPPORTED_IMAGE: {
    title: "Unsupported image",
    description: "Instagram only accepts JPG or PNG images. Change the file type and try again.",
  },
  IG_UNSUPPORTED_VIDEO: {
    title: "Unsupported video",
    description: "Instagram only accepts MP4 (H.264) videos. Change the file type and try again.",
  },
  IG_ASPECT_RATIO: {
    title: "Image shape not supported",
    description: "Instagram doesn't allow this image shape. Crop it between 4:5 (tall) and 1.91:1 (wide).",
  },
  IG_CAPTION_TOO_LONG: {
    title: "Caption too long",
    description: "Your caption is over Instagram's 2,200-character limit. Please make it shorter.",
  },
  IG_DAILY_LIMIT_REACHED: {
    title: "Daily post limit reached",
    description: "You've reached Instagram's limit of 100 posts a day. Please try again later.",
  },
  IG_CONTENT_BLOCKED: {
    title: "Post blocked by Instagram",
    description: "Instagram blocked this content. Edit the image or caption and try again.",
  },
  // ── Codes the backend sends with 5xx / provider failures ────────────────
  FEATURE_NOT_CONFIGURED: {
    title: "Not available yet",
    description: "This feature isn't ready on your account yet. Please contact support.",
    category: "server",
    severity: "warning",
    fixableBy: "support",
  },
  AI_UNAVAILABLE: {
    title: "AI temporarily busy",
    description: "Our AI is not responding right now. Please try again in a few minutes.",
    category: "server",
    severity: "warning",
    retryable: true,
  },
  STORAGE_NOT_CONFIGURED: {
    title: "Uploads not available",
    description: "We can't save files right now. Please contact support.",
    category: "server",
    severity: "error",
    fixableBy: "support",
  },
  CHAT_FILE_TYPE_NOT_ALLOWED: {
    title: "File type not allowed",
    description: "Send an image, video, audio or document instead.",
    category: "validation",
    severity: "warning",
  },
  CHAT_FILE_TOO_LARGE: {
    title: "File too large",
    description: "Choose a smaller file and try again.",
    category: "validation",
    severity: "warning",
  },
  CASHFREE_INVALID_CREDENTIALS: {
    title: "Cashfree keys not accepted",
    description: "Cashfree didn't accept these keys. Check your App ID and Secret Key, then try again.",
    category: "integration",
    severity: "warning",
  },
  POS_ORDER_REJECTED: {
    title: "Order not accepted",
    description: "Petpooja didn't accept this order, so it wasn't saved. Please check the order and try again.",
    category: "integration",
    severity: "error",
  },
  SHLINK_NOT_CONFIGURED: {
    title: "Short links not available",
    description: "Short links aren't ready on your account yet. Please contact support.",
    category: "server",
    severity: "warning",
    fixableBy: "support",
  },
  SHLINK_UNAVAILABLE: {
    title: "Short links temporarily busy",
    description: "We couldn't create the short link right now. Please try again in a few minutes.",
    category: "server",
    severity: "warning",
    retryable: true,
  },
  MAPPING_TABLE_MISSING: {
    title: "Not available yet",
    description: "Stage mapping isn't ready on your account yet. Please contact support.",
    category: "server",
    severity: "warning",
    fixableBy: "support",
  },
  COMPANY_CONFIG_INCOMPLETE: {
    title: "Company details missing",
    description: "Your company billing details aren't complete. Please contact support.",
    category: "billing",
    severity: "warning",
    fixableBy: "support",
  },
  PAYMENT_LINK_FAILED: {
    title: "Payment link not created",
    description: "We couldn't create the payment link. Please check your payment setup and try again.",
    category: "billing",
    severity: "error",
  },
  YOUTUBE_REFRESH_FAILED: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection has expired. Please connect your channel again.",
    category: "integration",
    severity: "warning",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix: "Your YouTube connection has expired. Ask your admin to connect the channel again.",
  },
  YOUTUBE_REFRESH_EMPTY_RESPONSE: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection has expired. Please connect your channel again.",
    category: "integration",
    severity: "warning",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix: "Your YouTube connection has expired. Ask your admin to connect the channel again.",
  },
  YOUTUBE_TOKEN_DECRYPT_FAILED: {
    title: "Reconnect YouTube",
    description: "We can't use your YouTube connection anymore. Please connect your channel again.",
    category: "integration",
    severity: "warning",
    fixableBy: "admin",
    action: { label: "Open Integrations", to: "/home/integrations" },
    whenCannotFix: "We can't use your YouTube connection anymore. Ask your admin to connect the channel again.",
  },
  META_ERROR: {
    title: "Meta didn't respond",
    description: "Meta couldn't complete this right now. Please try again in a few minutes.",
    category: "integration",
    severity: "error",
    retryable: true,
  },
  NO_ADS_SERVICE: {
    title: "Not available yet",
    description: "Ad tests aren't ready on your account yet. Please contact support.",
    category: "server",
    severity: "warning",
    fixableBy: "support",
  },
  LINKEDIN_NETWORK_ERROR: {
    title: "LinkedIn not responding",
    description: "We couldn't reach LinkedIn. Please try again in a few minutes.",
    category: "network",
    severity: "warning",
    retryable: true,
  },
  IMAGE_INIT_FAILED: {
    title: "Image upload failed",
    description: "LinkedIn didn't accept the image upload. Please try again or use another image.",
    category: "media",
    severity: "error",
  },
  CHUNK_UPLOAD_NETWORK_ERROR: {
    title: "Upload interrupted",
    description: "The video upload to TikTok stopped. Please check your internet and try again.",
    category: "network",
    severity: "warning",
    retryable: true,
  },
  S3_UPLOAD_FAILED: {
    title: "Upload failed",
    description: "We couldn't save your file. Please try again in a few minutes.",
    category: "media",
    severity: "error",
    retryable: true,
  },
};

// WHATSAPP_DISCONNECTED_<REASON>: one entry per reason the backend can send
// (backend whatsappConnectionLoss.js). Same short toast for every reason — the
// reason itself is in the banner, so the toast doesn't repeat it.
for (const reason of Object.keys(COEXISTENCE_REASON_COPY)) {
  CATALOG[`WHATSAPP_DISCONNECTED_${reason}`] = CATALOG.WHATSAPP_DISCONNECTED;
}

const PATTERN_MATCHERS: Array<{ test: RegExp; friendly: FriendlyError }> = [
  {
    test: /embedded credentials/i,
    friendly: CATALOG.COMMERCE_STORE_URL_INVALID,
  },
  {
    test: /2534022|2018278/,
    friendly: CATALOG.SOCIAL_WINDOW_EXPIRED,
  },
  {
    test: /2534022|outside (of )?allowed window|24[\s-]?hour/i,
    friendly: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  },
  {
    test: /\(#?190\)|access token.*(expire|invalid)|session has expired|OAuthException|token.*(expired|invalid)/i,
    friendly: CATALOG.NEEDS_RECONNECT,
  },
  {
    test: /\(#?10\)|\(#?200\)|do(es)? not have (the )?permission|insufficient permission/i,
    friendly: CATALOG.ROLE_FORBIDDEN,
  },
  {
    test: /application request limit reached|user request limit reached|\(#?4\)\s*application|\(#?80004\)/i,
    friendly: CATALOG.META_RATE_LIMITED,
  },
  {
    test: /rate limit|too many requests|#613|#80007|#131056|calls to this api have exceeded|spam rate limit|#131048/i,
    friendly: CATALOG.RATE_LIMITED,
  },

  {
    test: /\(#?131026\)|message undeliverable|recipient.*not.*whatsapp|not a (valid )?whatsapp user/i,
    friendly: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  },
  {
    test: /\(#?13104[79]\)|re-?engagement message|healthy ecosystem|maintain.*engagement/i,
    friendly: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  },
  {
    test: /\(#?131051\)|unsupported message type/i,
    friendly: {
      title: "Message type not supported",
      description: "This kind of message can't be sent here. Try text, a photo, or a template.",
    },
  },
  {
    // "Media upload error: image format is not in the allowed list" — a type
    // problem, not an expired file.
    test: /(format|file type|mime ?type)[^.]*(not (in the )?allowed|not supported|unsupported)|unsupported (file|media|image|video) (type|format)/i,
    friendly: CATALOG.MEDIA_TYPE_NOT_SUPPORTED,
  },
  {
    test: /\(#?131053\)|media upload error|failed to upload media/i,
    friendly: CATALOG.MEDIA_SEND_FAILED,
  },
  {
    test: /\(#?131052\)|\(#?131057\)|media download error|failed to download media/i,
    friendly: CATALOG.MEDIA_UPLOAD_EXPIRED,
  },
  {
    test: /\(#?132015\)|template is paused/i,
    friendly: CATALOG.TEMPLATE_PAUSED,
  },
  {
    test: /\(#?132016\)|template is disabled/i,
    friendly: CATALOG.TEMPLATE_DISABLED,
  },
  {
    test: /\(#?132001\)|template (name )?does not exist|template not found|no.*matching template/i,
    friendly: CATALOG.TEMPLATE_NOT_ON_META,
  },
  {
    test: /\(#?132000\)|number of (localizable_)?params|param(eter)?s? (do(es)? not match|mismatch)/i,
    friendly: {
      title: "Template details don't match",
      description:
        "The values you entered don't fit this template. Refresh the template and enter them again.",
    },
  },
  {
    test: /\(#?13200[57]\)|\(#?132012\)|template.*(policy|content|format).*(violat|invalid|mismatch)/i,
    friendly: CATALOG.CONTENT_POLICY_VIOLATION,
  },
  {
    test: /\(#?131031\)|\(#?368\)|account has been locked|temporarily blocked.*polic|business account.*restrict/i,
    friendly: CATALOG.ACCOUNT_RESTRICTED,
  },
  {
    test: /SQLState|ER_NO_SUCH_TABLE|Unknown column|SQL syntax|Table '[^']*' doesn'?t exist|doesn'?t exist; Error code|Deadlock found|ECONNREFUSED.*(mysql|3306|5432)/i,
    friendly: CATALOG.INTERNAL_ERROR,
  },
  {
    test: /Invalid JSON payload|Unknown name '[^']+' at|operations\[\d+\]\.(update|create|remove)/i,
    friendly: CATALOG.REQUEST_REJECTED,
  },
  {
    test: /no active razorpay connection|razorpay.*(not connected|isn'?t connected)|connect (your )?razorpay/i,
    friendly: CATALOG.RAZORPAY_NOT_CONNECTED,
  },
  {
    test: /no active payu connection|payu.*(not connected|isn'?t connected)|connect (your )?payu/i,
    friendly: CATALOG.PAYU_NOT_CONNECTED,
  },
  {
    test: /no active cashfree connection|cashfree.*(not connected|isn'?t connected)|connect (your )?cashfree/i,
    friendly: CATALOG.CASHFREE_NOT_CONNECTED,
  },
  {
    test: /no active connection found|no payment (gateway|provider|method).*(connected|configured)|payment (gateway|provider).*not connected/i,
    friendly: CATALOG.PAYMENT_NOT_CONNECTED,
  },
  {
    test: /not approved to access.*(REST|endpoint|protected)|protected customer data/i,
    friendly: {
      title: "Approval needed",
      description: "This connected app needs extra approval to see this data. Please contact support.",
    },
  },
  {
    test: /no active shopify connection|shopify.*not connected|no shopify connection/i,
    friendly: {
      title: "Shopify not connected",
      description: "No Shopify store is connected. Go to Integrations → Shopify to connect it.",
    },
  },
  {
    test: /failed to decrypt shopify|reconnect your shopify store/i,
    friendly: {
      title: "Reconnect your Shopify store",
      description: "Your Shopify connection needs a refresh. Go to Integrations → Shopify and connect again.",
    },
  },
  {
    test: /shopify api error 429|shopify.*rate.?limit|exceeded.*shopify.*limit/i,
    friendly: {
      title: "Shopify is busy",
      description: "Shopify got too many updates at once. Please wait a minute and try again.",
      severity: "info",
      retryable: true,
    },
  },
  {
    test: /shop(ify)?.*(is frozen|frozen|has been (closed|paused|suspended)|store is unavailable|is closed)/i,
    friendly: {
      title: "Shopify store unavailable",
      description:
        "Your Shopify store is paused or closed, often due to an unpaid bill. Fix it in Shopify, then try again.",
    },
  },
  {
    test: /shop_not_permitted/i,
    friendly: CATALOG.SHOP_NOT_PERMITTED,
  },
  {
    test: /(client credentials exchange|rejected these credentials|would not authorize these credentials)/i,
    friendly: CATALOG.CLIENT_CREDENTIALS_NOT_APPLICABLE,
  },
  {
    test: /shopify api error \d+/i,
    friendly: {
      title: "Shopify problem",
      description: "Shopify had a problem. Check the product details and try again.",
    },
  },
  {
    test: /quotaexceeded|youtube.*quota|daily.*quota.*(exceed|reached)|exceeded your.*quota/i,
    friendly: CATALOG.QUOTA_EXCEEDED,
  },
  {
    test: /youtube.*(refresh token|disconnect and reconnect|reconnect)|cannot refresh youtube token|no refresh_token persisted/i,
    friendly: CATALOG.NEEDS_RECONNECT,
  },
  {
    test: /\(#?100\)|invalid parameter|GraphMethodException/i,
    friendly: {
      title: "Couldn't complete this",
      description: "We couldn't complete this. Please refresh the page and try again.",
    },
  },
  {
    test: /account not registered|\(#?133010\)/i,
    friendly: CATALOG.WHATSAPP_NUMBER_NOT_REGISTERED,
  },
  {
    test: /ad ?account.*(is |been )?(disabled|closed)/i,
    friendly: CATALOG.AD_ACCOUNT_DISABLED,
  },
  {
    test: /unsupported (post|get) request|object with id .* does not exist|graph api documentation|developers\.facebook\.com\/docs/i,
    friendly: {
      title: "Something went wrong",
      description: "Your connected account may be missing a permission. Refresh, or reconnect it in Integrations.",
    },
  },
  {
    test: /webhook.*tim(ed|e)?.?out/i,
    friendly: CATALOG.WEBHOOK_TIMEOUT,
  },
  {
    test: /endpoint request timed out/i,
    friendly: CATALOG.TIMEOUT,
  },
  {
    test: /query must be less than \d+ characters|(prompt|description|query).*(exceed|too long|less than).*\d+ character/i,
    friendly: {
      title: "Description is too long",
      description: "Your flow description is too long. Please make it shorter.",
      category: "validation",
      severity: "warning",
    },
  },
  {
    test: /aspect ratio|image.*(shape|ratio).*(not supported|unsupported)/i,
    friendly: CATALOG.IG_ASPECT_RATIO,
  },
  {
    test: /caption.*(exceed|too long|2,?200)|2,?200[\s-]?character/i,
    friendly: CATALOG.IG_CAPTION_TOO_LONG,
  },
  {
    test: /\b100 posts\b|posts? per (24 hours|day)|daily.*post.*limit|reached.*post.*limit/i,
    friendly: CATALOG.IG_DAILY_LIMIT_REACHED,
  },
  {
    test: /timeout downloading media|failed to (download|fetch) media|media fetch failed/i,
    friendly: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  },
  {
    test: /flow is (in a )?blocked( state)?|flow has been blocked/i,
    friendly: CATALOG.FLOW_BLOCKED,
  },
  {
    test: /template (count |limit ).*(exceed|reach)|maximum.*templates|reached.*template limit/i,
    friendly: CATALOG.TEMPLATE_LIMIT_REACHED,
  },
  {
    test: /product not found in catalog|product_retailer_id|product.*(not (found|exist)|does not exist).*catalog|catalog.*does not (contain|exist)/i,
    friendly: CATALOG.PRODUCT_NOT_IN_CATALOG,
  },
  {
    test: /catalog .*not accessible with the current token|catalog access denied/i,
    friendly: CATALOG.CATALOG_NOT_ACCESSIBLE,
  },
  {
    test: /\b(HEADER|BODY|FOOTER|BUTTONS?)\b.*(component|format).*(require|missing|empty)|requires '?\w+'? field|component.*is required/i,
    friendly: CATALOG.TEMPLATE_INCOMPLETE,
  },

  {
    test: /upload failed \(413\)|\b413\b[^]*(upload|too large|payload|entity)|payload too large|request entity too large|request too long|file (is )?too large|exceeds.*(file )?size limit|content length exceeded/i,
    friendly: CATALOG.FILE_TOO_LARGE,
  },
  {
    test: /no image url provided|image url (is )?required|(product )?image (is )?required|please (upload|add) (a |an |the )?(product )?image/i,
    friendly: {
      title: "Product image needed",
      description: "This product has no image. Please upload one first.",
      category: "validation",
      severity: "warning",
    },
  },
  {
    test: /amount exceeds (the )?maximum|exceeds maximum amount|amount is too large|maximum amount allowed/i,
    friendly: CATALOG.AMOUNT_TOO_LARGE,
  },
  {
    test: /invalid razorpay credentials|razorpay.*(credential|key).*(invalid|rejected)|authentication failed.*razorpay/i,
    friendly: CATALOG.RAZORPAY_INVALID_CREDENTIALS,
  },
  {
    test: /failed to fetch(?!\s+\w)|networkerror|err_connection|(?<![a-z])load failed|net::|connection refused|socket hang ?up|ECONNREFUSED|ECONNRESET|ENOTFOUND|EHOSTUNREACH|ENETUNREACH|EPIPE|read ECONN|write ECONN/i,
    friendly: CATALOG.NETWORK_ERROR,
  },
  {
    test: /timed out|timeout|ETIMEDOUT|ECONNABORTED/i,
    friendly: CATALOG.TIMEOUT,
  },
  {
    test: /internal server error|bad gateway|service unavailable|gateway timeout|\b50[0-4]\b|HTTP\s*5\d\d/i,
    friendly: {
      title: "Temporarily unavailable",
      description: "We're temporarily having trouble completing your request. Please try again in a few minutes.",
    },
  },
  {
    test: /^[^.!?]{0,40}\bHTTP\s*4\d\d\b[^.!?]{0,12}$/i,
    friendly: {
      title: "Couldn't complete this",
      description: "We couldn't complete this. Please refresh the page and try again.",
      category: "validation",
      severity: "warning",
    },
  },
];

const META_CODE_MAP: Record<number, FriendlyError> = {
  0: CATALOG.NEEDS_RECONNECT,
  3: CATALOG.NEEDS_RECONNECT,
  10: CATALOG.INSTAGRAM_PERMISSION_DENIED,
  102: CATALOG.NEEDS_RECONNECT,
  110: CATALOG.NEEDS_RECONNECT,
  190: CATALOG.NEEDS_RECONNECT,
  200: CATALOG.INSTAGRAM_PERMISSION_DENIED,
  2: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  4: CATALOG.RATE_LIMITED,
  32: CATALOG.RATE_LIMITED,
  613: CATALOG.RATE_LIMITED,
  80004: CATALOG.RATE_LIMITED,
  80007: CATALOG.RATE_LIMITED,
  80008: CATALOG.RATE_LIMITED,
  133010: CATALOG.WHATSAPP_NUMBER_NOT_REGISTERED,
  130429: CATALOG.RATE_LIMITED,
  131048: CATALOG.RATE_LIMITED,
  131056: CATALOG.RATE_LIMITED,
  131057: CATALOG.RATE_LIMITED,
  368: CATALOG.ACCOUNT_RESTRICTED,
  131031: CATALOG.ACCOUNT_RESTRICTED,
  131042: CATALOG.ACCOUNT_BILLING_ISSUE,
  131000: CATALOG.INTERNAL_ERROR,
  131005: CATALOG.ROLE_FORBIDDEN,
  131008: CATALOG.VALIDATION_ERROR,
  131009: CATALOG.VALIDATION_ERROR,
  131016: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  131021: CATALOG.CANT_MESSAGE_RECIPIENT,
  131026: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  1013: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  131045: CATALOG.ACCOUNT_BILLING_ISSUE, // phone registration / certificate issue → admin action
  131047: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  131049: CATALOG.MARKETING_MESSAGE_BLOCKED,
  131050: CATALOG.USER_OPTED_OUT, // recipient stopped marketing messages
  131051: CATALOG.UNSUPPORTED_MESSAGE,
  131052: CATALOG.MEDIA_UPLOAD_EXPIRED,
  131053: CATALOG.MEDIA_SEND_FAILED,
  130472: CATALOG.MARKETING_MESSAGE_BLOCKED, // user in Meta experiment — message not delivered
  130497: CATALOG.COUNTRY_RESTRICTED,
  134011: CATALOG.PAYMENTS_TOS_NOT_ACCEPTED,
  135000: CATALOG.INTERNAL_ERROR,
  132000: CATALOG.TEMPLATE_PARAMS_MISMATCH,
  132001: CATALOG.TEMPLATE_NOT_ON_META,
  132005: CATALOG.TEMPLATE_TOO_LONG,
  132007: CATALOG.CONTENT_POLICY_VIOLATION,
  132012: CATALOG.TEMPLATE_PARAMS_MISMATCH,
  132015: CATALOG.TEMPLATE_PAUSED,
  132016: CATALOG.TEMPLATE_DISABLED,
  2388019: CATALOG.TEMPLATE_LIMIT_REACHED,
  2388040: CATALOG.TEMPLATE_FIELD_TOO_LONG,
  2388047: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388072: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388073: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388293: CATALOG.TEMPLATE_TOO_MANY_VARIABLES,
  2388299: CATALOG.TEMPLATE_VARIABLE_POSITION,
  132068: CATALOG.FLOW_BLOCKED,
  132069: CATALOG.FLOW_THROTTLED,
  9004: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207003: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  2207004: CATALOG.IG_IMAGE_TOO_LARGE,
  2207005: CATALOG.IG_UNSUPPORTED_IMAGE,
  2207006: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207008: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207009: CATALOG.IG_ASPECT_RATIO,
  2207010: CATALOG.IG_CAPTION_TOO_LONG,
  2207020: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207026: CATALOG.IG_UNSUPPORTED_VIDEO,
  2207032: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  2207042: CATALOG.IG_DAILY_LIMIT_REACHED,
  2207050: CATALOG.ACCOUNT_RESTRICTED,
  2207001: CATALOG.IG_CONTENT_BLOCKED,
  2207051: CATALOG.RATE_LIMITED,
  2207053: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  36001: CATALOG.IG_ASPECT_RATIO,
  36003: CATALOG.IG_ASPECT_RATIO,
  2534022: CATALOG.SOCIAL_WINDOW_EXPIRED,
  2018278: CATALOG.SOCIAL_WINDOW_EXPIRED,
};

const META_SUBCODE_MAP: Record<number, FriendlyError> = {
  33: CATALOG.INSTAGRAM_PERMISSION_DENIED, // code 100 + subcode 33 = missing scope
  1487756: CATALOG.AD_LOCATIONS_CONFLICT,
  458: CATALOG.NEEDS_RECONNECT, // user has not authorized application
  463: CATALOG.NEEDS_RECONNECT, // token expired
  467: CATALOG.NEEDS_RECONNECT, // invalid access token
  1404169: CATALOG.RATE_LIMITED, // IG DM 368 cooldown
  2207013: CATALOG.NEEDS_RECONNECT, // IG token/object expired
  2207052: CATALOG.MEDIA_UPLOAD_EXPIRED, // IG media download failed
  2534022: CATALOG.SOCIAL_WINDOW_EXPIRED, // IG/Messenger: outside the 24h window
  2018278: CATALOG.SOCIAL_WINDOW_EXPIRED, // Messenger: outside the standard window
};

function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function extractMetaCodeFromText(raw?: string): number | null {
  if (!raw) return null;
  const m = raw.match(/\(#?(\d{2,7})\)/);
  return m ? Number(m[1]) : null;
}

const HTTP_STATUS_MAP: Record<number, FriendlyError> = {
  400: {
    title: "Check your input",
    description: "Some details weren't accepted. Please check what you entered and try again.",
    category: "validation",
    severity: "warning",
  },
  401: CATALOG.UNAUTHORIZED,
  403: CATALOG.ROLE_FORBIDDEN,
  404: {
    title: "Not found",
    description: "We can't find this. It may have been moved or deleted.",
    category: "not_found",
    severity: "warning",
  },
  409: {
    title: "Already in use",
    description: "This clashes with something that already exists or is running. Refresh and try again.",
    category: "validation",
    severity: "warning",
  },
  413: CATALOG.FILE_TOO_LARGE,
  422: CATALOG.VALIDATION_ERROR,
  429: CATALOG.RATE_LIMITED,
  500: CATALOG.INTERNAL_ERROR,
  502: {
    title: "Temporarily unavailable",
    description: "We're temporarily having trouble completing your request. Please try again in a few minutes.",
    category: "server",
    severity: "error",
    retryable: true,
  },
  503: {
    title: "Temporarily unavailable",
    description: "We're temporarily having trouble completing your request. Please try again in a few minutes.",
    category: "server",
    severity: "error",
    retryable: true,
  },
  504: CATALOG.TIMEOUT,
};

function inferFromCodeShape(code: string): FriendlyError | null {
  const c = code.toUpperCase();
  if (c === "NOT_FOUND" || c.endsWith("_NOT_FOUND"))
    return {
      title: "Not found",
      description: "We can't find this. It may have been moved or deleted.",
      category: "not_found",
      severity: "warning",
    };
  if (/UNAUTHORIZED|TOKEN_EXPIRED|SESSION_|(^|_)AUTH(_|$)/.test(c))
    return { ...CATALOG.UNAUTHORIZED, category: "authentication", severity: "warning" };
  if (/FORBIDDEN|PERMISSION|NOT_ALLOWED|ACCESS_DENIED|(^|_)ROLE_/.test(c))
    return { ...CATALOG.ROLE_FORBIDDEN, category: "permission", severity: "warning" };
  if (/NETWORK|ECONN|FETCH/.test(c))
    return { ...CATALOG.NETWORK_ERROR };
  if (/RATE_LIMIT|TOO_MANY_REQUESTS|QUOTA|LIMIT_REACHED|LIMIT_EXCEEDED/.test(c))
    return { ...CATALOG.RATE_LIMITED, category: "rate_limit", severity: "info" };
  if (/NOT_CONFIGURED|CONFIG_MISSING|NOT_SET_UP|MISSING_CONFIG/.test(c))
    return { ...CATALOG.CONFIG_MISSING, category: "integration", severity: "warning" };
  if (/UNAVAILABLE|SERVICE_DOWN|(^|_)QUEUE_/.test(c))
    return { ...CATALOG.QUEUE_UNAVAILABLE, category: "server", severity: "warning", retryable: true };
  if (/TOO_LARGE|TOO_LONG|TOO_MANY|EXCEED/.test(c))
    return {
      title: "Too large",
      description: "This is bigger than allowed. Use a smaller value or file.",
      category: "validation",
      severity: "warning",
    };
  if (/INVALID_|_INVALID$|MALFORMED|(^|_)BAD_/.test(c))
    return { ...CATALOG.VALIDATION_ERROR, category: "validation", severity: "warning" };
  if (/MISSING_|_REQUIRED$|REQUIRED_|EMPTY_|_EMPTY$/.test(c))
    return {
      title: "Missing details",
      description: "Some required details are missing. Fill in the marked fields and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/CONFLICT|ALREADY_|_IN_USE$|(^|_)IN_USE/.test(c))
    return {
      title: "That already exists",
      description: "This already exists. Refresh and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/_FAILED$|FAILED_|_ERROR$|(^|_)ERROR_/.test(c))
    return {
      title: "Couldn't complete that",
      description: "Something went wrong. Please try again.",
      category: "unknown",
      severity: "error",
      retryable: true,
    };
  return null;
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

const RETRYABLE_TITLE_RE =
  /connection problem|you're offline|timed out|server problem|service (unavailable|busy)|slow down|temporarily/i;

function classify(friendly: FriendlyError): FriendlyError {
  const text = `${friendly.title} ${friendly.description}`.toLowerCase();

  let category = friendly.category;
  if (category == null) {
    if (/sign in|session|reconnect required|token/.test(text)) category = "authentication";
    else if (/permission|not allowed|ask an admin|ask your admin/.test(text)) category = "permission";
    else if (/rate limit|slow down|too many/.test(text)) category = "rate_limit";
    else if (/billing|balance|refund|payment/.test(text)) category = "billing";
    else if (/offline|connection problem|internet|reach the server/.test(text)) category = "network";
    else if (/not found/.test(text)) category = "not_found";
    else if (/server (problem|error)|unexpected error on our side|temporarily having trouble/.test(text)) category = "server";
    else if (/connect|integration|reconnect it/.test(text)) category = "integration";
    else if (/template|message|campaign|whatsapp|opted out|reply window/.test(text)) category = "messaging";
    else if (/invalid|required|check your input|too (large|long)|already exists/.test(text)) category = "validation";
    else if (/media|file|image|video|upload/.test(text)) category = "media";
    else category = "unknown";
  }

  let severity = friendly.severity;
  if (severity == null) {
    if (/restricted|banned|disabled|locked|billing issue/.test(text)) severity = "critical";
    else if (category === "rate_limit") severity = "info";
    else if (category === "validation" || category === "permission" || category === "not_found")
      severity = "warning";
    else severity = "error";
  }

  const retryable = friendly.retryable ?? RETRYABLE_TITLE_RE.test(friendly.title);

  return { ...friendly, severity, retryable, category };
}

function humanizeFieldName(key: string): string {
  const words = key.replace(/[_-]+/g, " ").trim();
  if (!words) return key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function detailsToText(details: unknown): string | undefined {
  if (details == null) return undefined;
  if (typeof details === "string") return details.trim() || undefined;
  if (typeof details === "object") {
    const d = details as Record<string, unknown>;
    const fieldMap =
      d.fields && typeof d.fields === "object" ? (d.fields as Record<string, unknown>) : null;
    if (fieldMap) {
      const parts = Object.entries(fieldMap)
        .filter(([, v]) => typeof v === "string" && (v as string).trim())
        .map(([k, v]) => `${humanizeFieldName(k)}: ${(v as string).trim()}`);
      if (parts.length) return parts.join("; ");
    }
    if (typeof d.message === "string" && d.message.trim()) return d.message.trim();
  }
  return undefined;
}

function humanizeCode(code: string): string {
  const words = code
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function looksLikeCode(s: string): boolean {
  return /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/.test(s.trim());
}

function defaultAskAdmin(category?: ErrorCategory): string {
  if (category === "integration" || category === "billing")
    return "This needs to be set up by an admin. Ask your admin to set it up.";
  return "You don't have permission to do this. Ask your admin for help.";
}

function applyAudience(friendly: FriendlyError, audience: Audience | undefined): FriendlyError {
  if (!audience || friendly.fixableBy !== "admin") return friendly;
  const privileged = audience === "owner" || audience === "super_admin" || audience === "admin";
  if (privileged) return friendly;
  return {
    ...friendly,
    description: friendly.whenCannotFix ?? defaultAskAdmin(friendly.category),
    action: undefined,
  };
}

// Meta's "Object with ID … does not exist / Unsupported get|post request" on a
// WhatsApp call: our token can no longer reach the WABA or phone number.
const META_ACCESS_LOST_RE = /unsupported (get|post) request|object with id .* does not exist/i;

export function whatsappAccessLostError(text: string | null | undefined): FriendlyError | null {
  return text && META_ACCESS_LOST_RE.test(text) ? CATALOG.WHATSAPP_ACCESS_LOST : null;
}

export function whatsappAccessLostMessage(text: string | null | undefined): string | null {
  return whatsappAccessLostError(text)?.description ?? null;
}

const GENERIC_BACKEND_CODES = new Set(["INTERNAL_SERVER_ERROR", "EXTERNAL_API_ERROR"]);

const DEV_JARGON_RE =
  /cannot read propert|is not a function|is not defined|undefined is not|null is not an object|\b(Type|Reference|Syntax|Range)Error\b|ECONN(REFUSED|RESET)|ETIMEDOUT|ENOTFOUND|EPIPE|socket hang up|getaddrinfo|\bER_[A-Z_]+\b|SQLSTATE|deadlock found|foreign key constraint|unexpected token|JSON at position|at \S+ \(.+:\d+:\d+\)|maximum call stack|heap out of memory/i;

export function resolveFriendlyError(
  input: ApiErrorLike | Error | string | null | undefined,
  fallback?: Partial<FriendlyError>,
  audience?: Audience,
  /** Extra text naming the integration (e.g. a notification title). */
  context?: string,
): FriendlyError & { raw: string | null } {
  const fb: FriendlyError = {
    title: fallback?.title || "Something went wrong",
    description:
      fallback?.description || "We couldn't finish that. Please try again.",
  };

  if (input == null) return { ...classify(fb), raw: null };

  let code: string | undefined;
  let metaCode: string | undefined;
  let rawMessage: string | undefined;
  let numericCode: number | null = null;
  let numericSubcode: number | null = null;
  let statusCode: number | null = null;

  const axiosOf = (o: unknown) => {
    const resp = (o as Record<string, unknown>)?.["response"] as Record<string, unknown> | undefined;
    const data = resp?.["data"] as Record<string, unknown> | undefined;
    return {
      error: typeof data?.["error"] === "string" ? (data["error"] as string) : undefined,
      message: typeof data?.["message"] === "string" ? (data["message"] as string) : undefined,
      details: typeof data?.["details"] === "string" ? (data["details"] as string) : undefined,
      code: data?.["code"],
      status: toNum(resp?.["status"]),
    };
  };

  let detailsText: string | undefined;

  if (typeof input === "string") {
    rawMessage = input;
  } else if (input instanceof Error) {
    const e = input as Error & Record<string, unknown>;
    const ax = axiosOf(e);
    rawMessage = ax.error ?? ax.message ?? input.message;
    detailsText = detailsToText(e.details) ?? ax.details;
    code = typeof e.code === "string" ? (e.code as string) : typeof ax.code === "string" ? (ax.code as string) : undefined;
    const errMetaCode = (e.meta as { code?: unknown } | null | undefined)?.code;
    metaCode = typeof errMetaCode === "string" && Object.prototype.hasOwnProperty.call(CATALOG, errMetaCode) ? errMetaCode : undefined;
    numericCode = toNum(e.code) ?? toNum(e.metaCode) ?? toNum(ax.code);
    numericSubcode = toNum(e.error_subcode) ?? toNum(e.metaErrorSubcode) ?? toNum(e.metaSubcode);
    statusCode = toNum(e.statusCode) ?? toNum(e.status) ?? ax.status;
  } else {
    const bag = input as Record<string, unknown>;
    const ax = axiosOf(bag);
    code = typeof input.code === "string" ? input.code : typeof ax.code === "string" ? ax.code : undefined;
    metaCode = typeof input.meta?.code === "string" ? input.meta.code : undefined;
    rawMessage = input.error || input.message || ax.error || ax.message || undefined;
    detailsText = detailsToText(input.details) ?? ax.details;
    numericCode = toNum(input.meta?.code) ?? toNum(input.code) ?? toNum(ax.code);
    numericSubcode = toNum(input.meta?.subcode) ?? toNum(bag.error_subcode);
    statusCode = toNum(input.statusCode) ?? toNum(bag.status) ?? ax.status;
  }

  // The backend's catch-all codes say nothing about the cause (AppError
  // defaults to INTERNAL_SERVER_ERROR even on a 400/404). Treat them as "no
  // code" so the status decides: a 4xx keeps its message, a 5xx gets the
  // server-trouble copy — instead of "_ERROR" shape-matching to generic copy.
  if (code && GENERIC_BACKEND_CODES.has(code)) code = undefined;
  if (metaCode && GENERIC_BACKEND_CODES.has(metaCode)) metaCode = undefined;

  // Which integration this is about — for turning a generic "Open
  // Integrations" button into "Open Shopify" etc.
  const integrationContext = [rawMessage, detailsText, code, metaCode, fallback?.title, fallback?.description]
    .filter(Boolean)
    .join(" ");

  const done = (friendly: FriendlyError, raw: string | null) => {
    const action = specificIntegrationAction(friendly.action, integrationContext, context);
    return {
      ...applyAudience(classify(action === friendly.action ? friendly : { ...friendly, action }), audience),
      raw,
    };
  };

  const metaDetail = typeof input === "string" ? null : metaErrorDetailOf(input);
  if (metaDetail) {
    const catalogAction = [metaCode, code].map((c) => (c ? CATALOG[c]?.action : undefined)).find(Boolean);
    const reconnectAction =
      metaDetail.code === 190 || code === "META_TOKEN_EXPIRED" || code === "ACCOUNT_NOT_CONNECTED"
        ? CATALOG.NEEDS_RECONNECT.action
        : undefined;
    const action = catalogAction ?? reconnectAction;
    return done(
      {
        title: fb.title,
        description: metaErrorText(metaDetail),
        category: isMetaRateLimit(metaDetail) ? "rate_limit" : "integration",
        ...(isMetaRateLimit(metaDetail) ? { retryable: true } : {}),
        ...(action ? { action } : {}),
      },
      metaDetail.message ?? metaDetail.title,
    );
  }

  const isGenericValidationCode =
    code === "VALIDATION_FAILED" ||
    code === "VALIDATION_ERROR" ||
    metaCode === "VALIDATION_FAILED" ||
    metaCode === "VALIDATION_ERROR";
  if (
    isGenericValidationCode &&
    detailsText &&
    detailsText.trim() &&
    !looksLikeCode(detailsText) &&
    !DEV_JARGON_RE.test(detailsText)
  ) {
    return done(
      {
        title: "Check your input",
        description: detailsText,
        category: "validation",
        severity: "warning",
      },
      detailsText,
    );
  }

  const RAW_MESSAGE_WINS_CODES = new Set([
    // Backend message carries Meta's own Health Status reason + fix.
    "WHATSAPP_SENDING_BLOCKED",
    "PUBLISH_FAILED_LOCALLY",
    "META_REJECTED_PRODUCT",
    "PRODUCT_HAS_NO_IMAGE",
    "TARGET_MISSING_WORKSPACE_ACCESS",
    "TARGET_MISSING_INBOX_ACCESS",
    "TARGET_MISSING_TICKET_ACCESS",
    "REASSIGN_SAME_MEMBER",
    "ADS_ADMIN_REQUIRED",
    "AD_ACCOUNT_LIMIT_REACHED",
    "AD_ACCOUNT_DISCONNECTED",
    "AD_ACCOUNT_STATE_UNAVAILABLE",
    "AD_IMAGE_TYPE",
    "AD_IMAGE_TOO_LARGE",
    "AD_IMAGE_EMPTY",
    "AD_IMAGE_URL",
    "AD_VIDEO_TYPE",
    "AD_VIDEO_EMPTY",
    "AD_VIDEO_TOO_LARGE",
    "AD_VIDEO_URL",
    "LEAD_FORM_INVALID",
    "LEAD_FORM_FILE_UNAVAILABLE",
    "META_REJECTED",
    "META_CREATE_UNCONFIRMED",
    "FIELD_NOT_EDITABLE",
    "PAUSE_LOOKUP_FAILED",
    "RAW_LEAD_DATASET_REQUIRED",
    "QUALITY_LEAD_UNAVAILABLE",
    "CAMPAIGN_NO_RECIPIENTS",
  ]);
  const rawWinsCode = [code, metaCode].find((c) => c && RAW_MESSAGE_WINS_CODES.has(c));
  if (
    rawWinsCode &&
    rawMessage &&
    rawMessage.trim() &&
    !DEV_JARGON_RE.test(rawMessage)
  ) {
    return done(
      {
        title: fb.title,
        description: rawMessage.trim(),
        ...(CATALOG[rawWinsCode]?.retryable ? { retryable: true } : {}),
      },
      rawMessage,
    );
  }

  for (const c of [metaCode, code]) {
    if (c && CATALOG[c]) return done(CATALOG[c], rawMessage ?? c);
  }

  const codeFromText = extractMetaCodeFromText(rawMessage);
  if (numericSubcode != null && META_SUBCODE_MAP[numericSubcode])
    return done(META_SUBCODE_MAP[numericSubcode], rawMessage ?? `#${numericSubcode}`);
  for (const nc of [numericCode, codeFromText]) {
    if (nc != null && META_CODE_MAP[nc]) return done(META_CODE_MAP[nc], rawMessage ?? `#${nc}`);
  }

  const searchText = [rawMessage, detailsText].filter(Boolean).join(" ");
  if (searchText) {
    for (const { test, friendly } of PATTERN_MATCHERS) {
      if (test.test(searchText)) {
        if (friendly === CATALOG.NETWORK_ERROR && isOffline()) return done(CATALOG.OFFLINE, searchText);
        return done(friendly, detailsText ?? rawMessage ?? null);
      }
    }
  }

  const shapeCode = metaCode || code || (rawMessage && looksLikeCode(rawMessage) ? rawMessage : undefined);
  if (shapeCode) {
    const inferred = inferFromCodeShape(shapeCode);
    if (inferred) return done(inferred, rawMessage ?? shapeCode);
  }

  if (statusCode != null && HTTP_STATUS_MAP[statusCode]) {
    const serverish = statusCode >= 500 || statusCode === 401 || statusCode === 403 || statusCode === 429;
    const hasHumanProse = !!(rawMessage && rawMessage.trim() && !looksLikeCode(rawMessage));
    if (serverish || !hasHumanProse) return done(HTTP_STATUS_MAP[statusCode], rawMessage ?? `HTTP ${statusCode}`);
  }

  if (shapeCode) {
    const humanized = humanizeCode(shapeCode);
    if (humanized) return done({ title: fb.title, description: `${humanized}.` }, rawMessage ?? shapeCode);
  }

  const isProse = (s?: string) => !!(s && s.trim() && !looksLikeCode(s));
  const proseCandidate = isProse(detailsText) ? detailsText : isProse(rawMessage) ? rawMessage : null;
  if (proseCandidate) {
    if (DEV_JARGON_RE.test(proseCandidate)) return done(fb, proseCandidate);
    const cleaned = proseCandidate.replace(/[:\-\s]+(undefined|null)\s*$/i, "").trim();
    if (cleaned) return done({ title: fb.title, description: cleaned }, proseCandidate);
  }

  return done(fb, rawMessage ?? null);
}
